import React, { useState, useEffect } from 'react';
import Slider from 'react-slick';
import { Link } from 'react-router-dom';
import api from '../../services/api';

import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

const UserTypes = () => {
  const [userTypes, setUserTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hoveredCard, setHoveredCard] = useState(null);

  // =========================================================
  // STATIC IMAGES
  // Images should be inside:
  // public/images/
  // =========================================================

  const USER_TYPE_IMAGES = {
    thirdParty:
      '/images/professional_service_banner_under_100kb.jpg.jpeg',

    partner:
      '/images/partner_400x300_under_100kb.jpg.jpeg',

    employee:
      '/images/skilled_employee_400x300_under_100kb.jpg.jpeg',

    business:
      '/images/business_for_sale_clean_under.jpeg',
  };

  
  const getUserTypeLabel = (userType) => {
  const userTypeLabels = {
    Partner: 'Strategic Partner',
    Business: 'Business Opportunities - Sell & Buy',
    '3rd Party Services': 'Professional Services',
    Employee: 'Talent & Workforce',
  };

  return userTypeLabels[userType] || userType;
};
  // =========================================================
  // GET STATIC IMAGE BASED ON USER TYPE
  // =========================================================

  const getUserTypeImage = (userType) => {
    if (!userType) {
      return null;
    }

    const normalizedType = userType
      .toString()
      .toLowerCase()
      .replace(/[-_]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    // Third Party
 if (normalizedType === '3rd party services') {
  return USER_TYPE_IMAGES.thirdParty;
}
    
if (normalizedType === 'partner') {
  return USER_TYPE_IMAGES.partner;
}

if (normalizedType === 'business') {
  return USER_TYPE_IMAGES.business;
}

if (normalizedType === 'employee') {
  return USER_TYPE_IMAGES.employee;
}



    return null;
  };

  // =========================================================
  // FETCH USER TYPES
  // =========================================================

  useEffect(() => {
    const fetchUserTypes = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await api.get('/user-types');

        if (!response.data.success) {
          throw new Error(
            response.data.message || 'Failed to fetch user types'
          );
        }

        setUserTypes(response.data.data || []);
      } catch (err) {
        console.error('Error fetching user types:', err);
        setError(err.message || 'Failed to fetch user types');
      } finally {
        setLoading(false);
      }
    };

    fetchUserTypes();
  }, []);

  // =========================================================
  // SLIDER SETTINGS
  // =========================================================

  const settings = {
    lazyLoad: 'ondemand',

    dots: true,

    infinite: true,

    speed: 800,

    slidesToShow: 3,

    slidesToScroll: 1,

    autoplay: true,

    autoplaySpeed: 3000,

    arrows: false,

    centerMode: false,

    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2,
        },
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1,
        },
      },
    ],

    customPaging: () => (
      <button
        type="button"
        className="w-8 h-8 rounded-full bg-gray-300 transition-colors duration-300 hover:bg-gray-600"
      />
    ),
  };

  // =========================================================
  // LOADING
  // =========================================================

  if (loading) {
    return (
      <div className="text-center py-8">
        Loading user types...
      </div>
    );
  }

  // =========================================================
  // ERROR
  // =========================================================

  if (error) {
    return (
      <div className="text-center py-8 text-red-500">
        Error: {error}
      </div>
    );
  }

  // =========================================================
  // MAIN UI
  // =========================================================

  return (
    <section className="case_studies py-[50px] bg-gray-100 mb-4">
      <div className="container mx-auto">

        {/* =====================================================
            HEADING
        ====================================================== */}

        <div className="row flex justify-center">
          <div className="col w-full text-center">
            <h1 className="text-4xl font-semibold leading-tight">
             Click the links below to explore Services, Resources & Opportunities to support your business.

           
            </h1>
          </div>
        </div>

        {/* =====================================================
            USER TYPE SLIDER
        ====================================================== */}

        <div className="mt-8">

          {userTypes.length > 0 ? (
            <Slider
              {...settings}
              className="carousel-two relative"
            >

              {userTypes.map((type) => {

                const userTypeImage = getUserTypeImage(
                  type.user_type
                );

                const isHovered =
                  hoveredCard === type.user_type;

                return (
                  <div
                    key={type.user_type}
                    className="px-4"
                  >

                    <Link
                      to={`/users?type=${encodeURIComponent(
                        type.user_type
                      )}`}
                      className="block"
                    >

                      <div
                        onMouseEnter={() =>
                          setHoveredCard(type.user_type)
                        }
                        onMouseLeave={() =>
                          setHoveredCard(null)
                        }
                        style={{
                          backgroundColor: isHovered
                            ? '#f9fafb'
                            : '#ffffff',

                          borderRadius: '12px',

                          boxShadow: isHovered
                            ? '0 10px 28px rgba(0,0,0,0.18)'
                            : '0 4px 12px rgba(0,0,0,0.1)',

                          transition:
                            'all 0.3s ease-in-out',

                          padding: '10px',

                          marginBottom: '20px',

                          cursor: 'pointer',

                          transform: isHovered
                            ? 'translateY(-6px)'
                            : 'translateY(0)',
                        }}
                      >

                        {/* =================================================
                            IMAGE
                        ================================================== */}

                        <figure
                          className="overflow-hidden"
                          style={{
                            margin: 0,
                            borderRadius: '8px',
                          }}
                        >

                          {userTypeImage ? (

                            <img
                              loading="lazy"
                              src={userTypeImage}
                              alt={type.user_type}
                              className="w-full"
                              style={{
                                width: '100%',
                                height: '250px',
                                objectFit: 'cover',
                                display: 'block',
                              }}

                              onError={(e) => {
                                console.error(
                                  'Image failed to load:',
                                  userTypeImage
                                );

                                e.currentTarget.style.display =
                                  'none';
                              }}
                            />

                          ) : (

                            <div
                              className="w-full flex items-center justify-center bg-gray-200"
                              style={{
                                width: '100%',
                                height: '250px',
                                color: '#6b7280',
                              }}
                            >
                              No Image Available
                            </div>

                          )}

                        </figure>

                        {/* =================================================
                            USER TYPE NAME
                        ================================================== */}

                        <div className="p-4 text-center">

                          <h6
                            className="text-lg font-bold transition-colors duration-300"
                            style={{
                              color: isHovered
                                ? '#2563eb'
                                : '#1f2937',

                              textDecoration: isHovered
                                ? 'underline'
                                : 'none',

                              marginBottom: 0,
                            }}
                          >

                     {getUserTypeLabel(type.user_type)} ({type.count || 0})

                          </h6>

                        </div>

                      </div>

                    </Link>

                  </div>
                );
              })}

            </Slider>

          ) : (

            <div className="text-center py-8">
              No user types available
            </div>

          )}

        </div>

      </div>
    </section>
  );
};

export default UserTypes;